"use client";

import { useCallback, useSyncExternalStore } from "react";
import { useStudio } from "@/contexts/studio-context";
import type { Piano } from "@/lib/piano/piano";

const EMPTY: ReadonlySet<number> = new Set();
const NO_STUDIO_UNSUBSCRIBE = () => {};

/**
 * Un magasin par piano : l'ensemble des notes enfoncées, recalculé **une fois
 * par événement** et partagé entre tous les abonnés.
 *
 * Avant, chaque appelant du hook (dix sites, jusqu'à trois montés à la fois)
 * s'abonnait lui-même au bus du piano et clonait son propre `Set` à chaque
 * touche enfoncée ou relâchée. Ici un seul écouteur nourrit un instantané
 * commun, et `useSyncExternalStore` ne re-rend que quand il change.
 *
 * Le magasin ne tient au piano que tant qu'il a des abonnés : le dernier parti
 * retire l'écouteur, et l'instantané est resemé depuis `getPressedMidis()` au
 * prochain abonnement — une note tenue pendant un remontage n'est pas perdue.
 *
 * Tenu ici, dans une `WeakMap`, plutôt que sur `Piano` : la classe n'a pas à
 * connaître React, et la clé faible suit la vie de l'instrument.
 */
interface Store {
  snapshot: ReadonlySet<number>;
  listeners: Set<() => void>;
  release: (() => void) | null;
}

const stores = new WeakMap<Piano, Store>();

function storeFor(piano: Piano): Store {
  let store = stores.get(piano);

  if (store === undefined) {
    store = { snapshot: EMPTY, listeners: new Set(), release: null };
    stores.set(piano, store);
  }

  return store;
}

function attach(piano: Piano, store: Store): void {
  store.snapshot = new Set(piano.getPressedMidis().keys());

  store.release = piano.on((event) => {
    if (event.type !== "press" && event.type !== "release") {
      return;
    }

    const next = new Set(store.snapshot);

    if (event.type === "press") {
      next.add(event.note.toMidi());
    } else {
      next.delete(event.note.toMidi());
    }

    store.snapshot = next;

    for (const listener of store.listeners) {
      listener();
    }
  });
}

function subscribeTo(piano: Piano, listener: () => void): () => void {
  const store = storeFor(piano);

  if (store.listeners.size === 0) {
    attach(piano, store);
  }

  store.listeners.add(listener);

  return () => {
    store.listeners.delete(listener);

    if (store.listeners.size === 0) {
      store.release?.();
      store.release = null;
    }
  };
}

export function useActivePianoNotes(): ReadonlySet<number> {
  const { studio } = useStudio();
  const piano = studio?.getPiano() ?? null;

  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      if (piano === null) {
        return NO_STUDIO_UNSUBSCRIBE;
      }

      return subscribeTo(piano, onStoreChange);
    },
    [piano],
  );

  const getSnapshot = useCallback(
    () => (piano === null ? EMPTY : storeFor(piano).snapshot),
    [piano],
  );

  return useSyncExternalStore(subscribe, getSnapshot, () => EMPTY);
}
